import { Blocks, MessageCircle, Smartphone } from "lucide-react";
import rosto from "@/assets/fundador/vinicius-rosto.webp";
import retrato from "@/assets/fundador/vinicius-retrato.webp";
import { cn } from "@/lib/utils";
import { CONTAINER, SECTION_Y } from "./ui-tokens";

/**
 * O rosto por trás do Ecclesiato.
 *
 * Tudo aqui é fato contado pelo Vinicius em 06/10/2026: a igreja, o tempo de
 * casa, a formação, quem deu a ideia, o que a IEQ Central usa, quem atende o
 * suporte. Não acrescente frase que ele não tenha dito; o valor desta peça é
 * ser verificável por quem frequenta a mesma igreja.
 *
 * A história inteira mora na /about. Na home entra só a assinatura (uma foto e
 * duas linhas), porque a home já é longa e o rosto não precisa de seção para
 * fazer o trabalho dele: assumir a promessa no ponto em que a pessoa decide.
 *
 * A IEQ Central é ao mesmo tempo o case da home e a igreja do fundador. Isso é
 * dito aqui e no case (case-ieq.tsx), de propósito: case escondendo a autoria
 * parece depoimento plantado (ver marketing/reels/GUIA-DE-REELS.md, seção 2).
 */
const NOME = "Vinicius Graciano";

const compromissos = [
  {
    icon: MessageCircle,
    title: "Quem responde o suporte sou eu",
    desc: "A dúvida da sua igreja chega direto em quem escreveu o sistema, no WhatsApp.",
  },
  {
    icon: Blocks,
    title: "Faltou um recurso? Me conte",
    desc: "Se ele fizer sentido para a sua igreja e para as outras que usam o Ecclesiato, eu construo.",
  },
  {
    icon: Smartphone,
    title: "Não tem app nas lojas, e é de propósito",
    desc: "Membro não deveria baixar um aplicativo que quase nunca vai abrir. Tudo o que ele precisa abre por link, no navegador do celular.",
  },
];

/** A história completa, para a /about. */
export function FounderStory() {
  return (
    <section aria-labelledby="fundador-titulo" className={SECTION_Y}>
      <div className={`${CONTAINER} grid gap-12 lg:grid-cols-12 lg:gap-16`}>
        <figure className="lg:col-span-5">
          <img
            src={retrato}
            alt={`${NOME}, criador do Ecclesiato, falando para os jovens da IEQ Central de Piracicaba`}
            className="lp-img aspect-4/5 w-full rounded-3xl object-cover"
            width={800}
            height={1000}
            loading="lazy"
          />
        </figure>

        <div className="lg:col-span-7">
          <h2
            id="fundador-titulo"
            className="text-3xl leading-[1.1] font-semibold tracking-tight text-balance text-foreground md:text-[2.625rem]"
          >
            O Ecclesiato nasceu dentro de uma igreja.{" "}
            <span className="text-muted-foreground">E ela usa até hoje.</span>
          </h2>

          <div className="mt-6 flex flex-col gap-4 text-base leading-relaxed text-pretty text-muted-foreground md:text-lg">
            <p>
              Meu nome é {NOME}. Sou membro da IEQ Central de Piracicaba há
              cinco anos e sirvo na liderança de jovens. Sou técnico em
              informática e estudo Análise e Desenvolvimento de Sistemas.
            </p>
            <p>
              A ideia veio do meu primo, o pastor Bruno. Ele sabia que eu era
              desenvolvedor e, meses antes, me disse que as igrejas precisavam
              de um sistema feito para elas. Quando o pastor Leonardo Medeiros, pastor
              auxiliar da IEQ Central, disse que a igreja precisava de um
              sistema para gerenciar tudo, e que os que ele tinha visto eram
              complexos demais, o Ecclesiato já estava engatilhado.
            </p>
            <p>
              Hoje a IEQ Central usa no dia a dia pessoas, visitantes,
              ministérios, escalas, eventos, chamadas, apelos, cantina e bio
              links. Os líderes de ministério que começaram a usar não pararam
              mais. Antes, passavam um mês perguntando a disponibilidade de
              cada um. Hoje o voluntário marca no link, e em menos de uma
              semana a escala está pronta, com tudo o que os ministérios e as
              escalas do sistema oferecem.
            </p>
          </div>

          <ul className="mt-10 flex flex-col gap-5">
            {compromissos.map((item) => (
              <li key={item.title} className="flex gap-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/8">
                  <item.icon
                    className="size-4.5 text-primary"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                </span>
                <div>
                  <p className="font-semibold text-foreground">{item.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {item.desc}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/**
 * A assinatura da home: foto, uma frase em primeira pessoa e o nome.
 * `tone="band"` é para a faixa escura do fechamento.
 */
export function FounderSignature({
  tone = "default",
  className,
}: {
  tone?: "default" | "band";
  className?: string;
}) {
  const band = tone === "band";

  return (
    <figure className={cn("flex items-start gap-4", className)}>
      <img
        src={rosto}
        alt=""
        className="size-12 shrink-0 rounded-full object-cover"
        width={48}
        height={48}
        loading="lazy"
      />
      <div>
        <blockquote
          className={cn(
            "text-base leading-relaxed text-pretty",
            band ? "text-lp-band-foreground/90" : "text-foreground",
          )}
        >
          “Construí o Ecclesiato dentro da IEQ Central, a igreja onde sirvo.
          Quem te responde no WhatsApp sou eu.”
        </blockquote>
        <figcaption
          className={cn(
            "mt-2 text-sm",
            band ? "text-lp-band-foreground/65" : "text-muted-foreground",
          )}
        >
          <span
            className={cn(
              "font-semibold",
              band ? "text-lp-band-foreground" : "text-foreground",
            )}
          >
            {NOME}
          </span>{" "}
          · criador do Ecclesiato
        </figcaption>
      </div>
    </figure>
  );
}
